import { useEffect, useMemo, useState } from "react";
import { MapContainer, Marker, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import { X, MapPin, Check, MapPinOff } from "lucide-react";
import MapTileLayer from "../MapTileLayer";
import { Button } from "../ui/button";

const esc = (s) => String(s ?? "").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

function markerIcon(d, on) {
  const farbe = !d.pi?.online ? "#9CA3AF" : on ? "#C026D3" : "#FFFFFF";
  const rand = on ? "#FFFFFF" : d.pi?.online ? "#C026D3" : "#FFFFFF";
  const text = on ? "#FFFFFF" : "#111827";
  const bg = on ? "#C026D3" : "rgba(255,255,255,0.95)";
  return L.divIcon({
    className: "mast-karte-marker",
    html: `<div style="display:flex;align-items:center;gap:6px;transform:translate(-11px,-11px)">
      <div style="width:22px;height:22px;border-radius:50%;background:${farbe};border:3px solid ${rand};box-shadow:0 2px 6px rgba(0,0,0,.4);display:flex;align-items:center;justify-content:center;color:#fff;font-size:13px;font-weight:700">${on ? "✓" : ""}</div>
      <div style="white-space:nowrap;padding:2px 7px;border-radius:9999px;background:${bg};color:${text};font:600 12px system-ui,sans-serif;box-shadow:0 1px 4px rgba(0,0,0,.3)">${esc(d.name)}${d.pi?.online ? "" : " · offline"}</div>
    </div>`,
    iconSize: [0, 0],
    iconAnchor: [0, 0],
  });
}

// Karte auf alle Masten zoomen (einmal beim Oeffnen)
function Einpassen({ punkte }) {
  const map = useMap();
  useEffect(() => {
    setTimeout(() => map.invalidateSize(), 50);
    if (punkte.length === 1) map.setView(punkte[0], 17);
    else if (punkte.length > 1) map.fitBounds(L.latLngBounds(punkte), { padding: [50, 50], maxZoom: 18 });
  }, [map]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

// Vollbild-Karte (Handy) bzw. grosses Fenster: Masten per Antippen an-/abwaehlen. Beim Schliessen gilt die Auswahl als Ziel.
// selected = [] bedeutet "alle Masten" (wie MastAuswahl).
export function MastKarte({ devices, selected, onClose }) {
  const [wahl, setWahl] = useState(() => new Set(selected.length ? selected : devices.map(d => d.id)));
  const mitPos = useMemo(() => devices.filter(d => Array.isArray(d.position) && d.position[0] != null), [devices]);
  const ohnePos = devices.filter(d => !mitPos.includes(d));
  const punkte = mitPos.map(d => [Number(d.position[0]), Number(d.position[1])]);

  const toggle = (id) => setWahl(w => { const n = new Set(w); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const schliessen = () => {
    // keine Auswahl -> bisherige Auswahl behalten; alle gewaehlt -> "Alle Masten"
    if (wahl.size === 0) { onClose(null); return; }
    onClose(wahl.size === devices.length ? [] : devices.filter(d => wahl.has(d.id)).map(d => d.id));
  };
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") schliessen(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    // Seite dahinter nicht scrollen, solange die Karte offen ist
    const ov = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ov; };
  }, []);

  return (
    <div className="fixed inset-0 z-[1000] bg-black/50 flex items-stretch sm:items-center justify-center sm:p-6" data-testid="mast-karte">
      <div className="bg-white w-full sm:max-w-4xl sm:rounded-xl shadow-xl flex flex-col overflow-hidden h-full sm:h-[85vh]">
        <div className="flex items-center gap-2 px-3 sm:px-4 py-2.5 border-b border-gray-200">
          <MapPin className="w-4 h-4 text-fuchsia-600 shrink-0" />
          <div className="min-w-0 flex-1">
            <h2 className="text-sm font-bold text-gray-900">Masten auf der Karte wählen</h2>
            <p className="text-[11px] text-gray-500">Mast antippen = an/aus · {wahl.size} von {devices.length} gewählt</p>
          </div>
          <button type="button" onClick={schliessen} className="w-10 h-10 -mr-1 rounded-lg flex items-center justify-center text-gray-500 hover:bg-gray-100" title="Schließen" data-testid="mast-karte-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 min-h-0 relative">
          {mitPos.length > 0 ? (
            <MapContainer center={punkte[0]} zoom={16} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
              <MapTileLayer />
              <Einpassen punkte={punkte} />
              {mitPos.map(d => (
                <Marker key={d.id} position={[Number(d.position[0]), Number(d.position[1])]} icon={markerIcon(d, wahl.has(d.id))}
                  eventHandlers={{ click: () => toggle(d.id) }}>
                  <Tooltip direction="top" offset={[0, -14]}>{d.name} – {wahl.has(d.id) ? "gewählt" : "nicht gewählt"}</Tooltip>
                </Marker>
              ))}
            </MapContainer>
          ) : (
            <div className="h-full flex flex-col items-center justify-center text-sm text-gray-500 gap-2 p-6 text-center">
              <MapPinOff className="w-8 h-8 text-gray-300" /> Für die Masten dieser Veranstaltung ist noch kein GPS-Standort bekannt.
            </div>
          )}
        </div>

        <div className="border-t border-gray-200 p-3 space-y-2">
          <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto" data-testid="mast-karte-liste">
            {devices.map(d => {
              const on = wahl.has(d.id);
              return (
                <button key={d.id} type="button" onClick={() => toggle(d.id)} data-testid={`mast-karte-chip-${d.serial_number}`}
                  className={`inline-flex items-center gap-1 px-3 py-2 rounded-full border text-xs font-medium ${on ? "bg-fuchsia-600 border-fuchsia-600 text-white" : "bg-white border-gray-200 text-gray-600"}`}>
                  {on && <Check className="w-3 h-3" />}
                  <span className={`w-2 h-2 rounded-full ${d.pi?.online ? "bg-emerald-400" : "bg-gray-300"}`} />
                  {d.name}{ohnePos.includes(d) ? " · ohne GPS" : ""}{d.pi?.online ? "" : " · offline"}
                </button>
              );
            })}
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => setWahl(new Set(devices.map(d => d.id)))} className="h-11 sm:h-9" data-testid="mast-karte-alle">Alle</Button>
            <Button type="button" variant="outline" onClick={() => setWahl(new Set())} className="h-11 sm:h-9" data-testid="mast-karte-keine">Keine</Button>
            <Button type="button" onClick={schliessen} disabled={wahl.size === 0} className="flex-1 h-11 sm:h-9 bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-semibold" data-testid="mast-karte-fertig">
              {wahl.size === 0 ? "Mindestens einen Mast wählen" : `Fertig – ${wahl.size === devices.length ? "alle Masten" : `${wahl.size} Mast${wahl.size === 1 ? "" : "en"}`}`}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
